import { AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { logError } from "shared/services/ErrorReporting";

/**
 * Shown when the signed-in session's role/BU claims disagree with the user doc
 * and a token refresh already failed to reconcile them (`claimsStale`).
 *
 * Without this banner the only symptom is a BU admin's requests page
 * rendering nothing: the security rules refuse a query built from the stale
 * claim, and a refused query is indistinguishable from an empty one.
 * `business_unit_id` is minted only at sign-in, so signing in again is the one
 * action guaranteed to re-derive the claims from the doc — which makes the fix
 * one click, and makes saying so worth a banner.
 *
 * Deliberately conditioned on a refresh having been tried and failed, not on a
 * prediction that it would: what wins between a session claim and a persisted
 * custom claim is not something we have established.
 *
 * Rendered at the root above every shell (like ImpersonationBanner), because
 * the admin areas suppress the Navbar and would otherwise never show it.
 */
export function StaleSessionBanner() {
  const { claimsStale, signOut } = useAuth();
  const { t } = useTranslation("common");

  if (!claimsStale) return null;

  return (
    <div
      role="alert"
      data-testid="banner-stale-session"
      className="flex flex-wrap items-center justify-between gap-3 bg-system-warning px-4 py-2 text-sm text-white"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <p>
          <span className="font-semibold">{t("staleSession.title")}</span>{" "}
          {t("staleSession.body")}
        </p>
      </div>
      <Button
        variant="outline"
        className="shrink-0 border-white/40 bg-transparent text-white hover:bg-white/10"
        onClick={() => {
          void signOut().catch(logError);
        }}
        data-testid="button-stale-session-reauth"
      >
        {t("staleSession.action")}
      </Button>
    </div>
  );
}
